import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { orgLabel } from "@/lib/constants";
import { QueuePager } from "@/components/admin/QueuePager";

export default function VerificationsTab() {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState("");
  const [page, setPage] = useState({ total: 0, hasMore: false, loading: false });

  const load = useCallback((append = false, offset = 0) => {
    setPage((p) => ({ ...p, loading: true }));
    api.get("/admin/verifications", { params: { skip: append ? offset : 0, limit: 100 } })
      .then(({ data }) => {
        setQueue((prev) => (append ? [...prev, ...(data.queue || [])] : data.queue || []));
        setPage({ total: data.total ?? (data.queue || []).length, hasMore: !!data.has_more, loading: false });
      }).catch(() => setPage((p) => ({ ...p, loading: false })));
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (uid, approved, tier) => {
    setBusy(uid);
    try {
      await api.post("/admin/verifications/decide", { profile_user_id: uid, approved, tier });
      toast.success(approved ? "Approved" : "Rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="admin-verification-queue">
      <QueuePager shown={queue.length} total={page.total} hasMore={page.hasMore} loading={page.loading}
        onMore={() => load(true, queue.length)} testid="verifications-pager" />
      {queue.length === 0 ? (
        <div className="border border-dashed border-slate-200 bg-white rounded-lg p-12 text-center text-slate-400">No pending verification requests.</div>
      ) : queue.map((p) => (
        <div key={p.user_id} className="border border-slate-200 bg-white rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid={`verify-row-${p.user_id}`}>
          <div className="h-11 w-11 rounded-lg bg-[#0A0F1C] flex items-center justify-center font-heading font-bold text-white">{p.org_name?.[0] || "B"}</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[#0A0F1C]">{p.org_name}</div>
            <div className="text-xs text-slate-500">{orgLabel(p.org_type)} · {p.industry} · requested <span className="font-medium capitalize">{p.verification_tier}</span></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => decide(p.user_id, true, p.verification_tier || "verified")} disabled={busy === p.user_id} data-testid={`verify-approve-${p.user_id}`} className="h-9 px-4 rounded-md bg-[#16A34A] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-green-700 disabled:opacity-60"><CheckCircle2 size={15} /> Approve</button>
            <button onClick={() => decide(p.user_id, false)} disabled={busy === p.user_id} data-testid={`verify-reject-${p.user_id}`} className="h-9 px-4 rounded-md border border-red-200 text-red-600 text-sm font-medium flex items-center gap-1.5 hover:bg-red-50 disabled:opacity-60"><XCircle size={15} /> Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}
